import React, { useState, useEffect } from "react";
import axios from "axios";
import { useLanguage } from "../i18n/LanguageContext";
import { API, BUSINESS, IMG } from "../lib/constants";
import { MapPin, Phone, Instagram, Clock, Check, Loader2 } from "lucide-react";

const TikTokIcon = ({ size = 16 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
        <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
    </svg>
);
import SEOHead from "../components/SEOHead";

const Contact = () => {
    const { t, lang } = useLanguage();
    const c = t.contact;
    const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
    const [status, setStatus] = useState({ submitting: false, success: false, error: "" });

    // Load GHL booking widget script
    useEffect(() => {
        const script = document.createElement("script");
        script.src = "https://link.proaimarketers.com/js/form_embed.js";
        script.type = "text/javascript";
        document.body.appendChild(script);
        return () => {
            if (document.body.contains(script)) document.body.removeChild(script);
        };
    }, []);

    const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

    const onSubmit = async (e) => {
        e.preventDefault();
        setStatus({ submitting: true, success: false, error: "" });
        try {
            await axios.post(`${API}/contact`, { ...form, language: lang });
            setStatus({ submitting: false, success: true, error: "" });
            setForm({ name: "", email: "", phone: "", message: "" });
        } catch (err) {
            setStatus({ submitting: false, success: false, error: t.common.errorSubmit });
        }
    };

    return (
        <>
            <SEOHead page="contact" />
            {/* Hero - About page style with still image from video */}
            <section
                data-testid="contact-hero"
                className="bg-[#3D3D24] text-[#FFFFF3] overflow-hidden"
            >
                <div className="max-w-[1400px] mx-auto grid lg:grid-cols-2 gap-0">
                    <div className="order-2 lg:order-1 px-6 md:px-12 lg:px-16 py-16 md:py-24 lg:py-28 flex flex-col justify-center">
                        <div className="font-label text-[11px] text-[#D4A95A] mb-5">{c.heroOverline}</div>
                        <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[0.98] mb-7 max-w-3xl">
                            {c.heroTitle}
                        </h1>
                        <p className="text-base md:text-lg opacity-85 max-w-2xl leading-relaxed">
                            {c.heroSub}
                        </p>
                    </div>
                    <div className="order-1 lg:order-2 relative">
                        <video
                            src={IMG.videoLocation}
                            muted
                            playsInline
                            poster=""
                            preload="metadata"
                            className="w-full h-full object-cover object-center min-h-[420px] lg:min-h-[560px]"
                        />
                    </div>
                </div>
            </section>

            {/* GHL Booking Widget */}
            <section className="bg-[#FFFFF3] py-20 md:py-28">
                <div className="max-w-[1100px] mx-auto px-6">
                    <div className="text-center mb-10">
                        <div className="font-label text-[11px] text-[#D4A95A] mb-4">
                            {lang === "es" ? "RESERVA TU CITA" : "BOOK YOUR APPOINTMENT"}
                        </div>
                        <h2 className="font-serif text-4xl md:text-5xl text-[#3D3D24] leading-[1.05]">
                            {lang === "es" ? "Elige tu servicio" : "Choose Your Service"}
                        </h2>
                        <p className="text-[#3D3D24]/70 text-base mt-4 max-w-xl mx-auto">
                            {lang === "es"
                                ? "Selecciona un servicio a continuación para ver disponibilidad y reservar directamente."
                                : "Select a service below to see availability and book directly online."}
                        </p>
                    </div>
                    <div data-testid="booking-widget" className="border border-[#3D3D24]/10 bg-white">
                        <iframe
                            src="https://link.proaimarketers.com/widget/service-menu/6a612b5ef00d5d64da3c1449"
                            style={{ width: "100%", border: "none", overflow: "hidden", minHeight: "600px" }}
                            scrolling="no"
                            id="6a612b5ef00d5d64da3c1449_1784754903164"
                            title="Book an Appointment - Beauty Ink LV"
                        />
                    </div>
                </div>
            </section>

            {/* Body */}
            <section className="bg-[#FFFFF3] py-24 md:py-32">
                <div className="max-w-[1400px] mx-auto px-6 grid lg:grid-cols-[1fr_1.2fr] gap-14 lg:gap-20">
                    {/* Details */}
                    <div className="space-y-10">
                        <div data-testid="contact-info-address" className="flex items-start gap-5 pb-8 border-b border-[#3D3D24]/15">
                            <div className="w-12 h-12 bg-[#3D3D24] text-[#D4A95A] flex items-center justify-center shrink-0">
                                <MapPin size={18} />
                            </div>
                            <div>
                                <div className="font-label text-[10px] text-[#D4A95A] mb-2">{t.common.address}</div>
                                <a
                                    data-testid="contact-address"
                                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(BUSINESS.address)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="font-serif text-xl text-[#3D3D24] leading-snug hover:text-[#D4A95A] transition-colors block"
                                >
                                    {BUSINESS.address}
                                </a>
                                <a
                                    data-testid="contact-directions"
                                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(BUSINESS.address)}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm font-label mt-3 inline-block text-[#3D3D24] hover:text-[#D4A95A] tracking-[0.15em]"
                                >
                                    Get Directions →
                                </a>
                            </div>
                        </div>

                        <div data-testid="contact-info-phone" className="flex items-start gap-5 pb-8 border-b border-[#3D3D24]/15">
                            <div className="w-12 h-12 bg-[#3D3D24] text-[#D4A95A] flex items-center justify-center shrink-0">
                                <Phone size={18} />
                            </div>
                            <div>
                                <div className="font-label text-[10px] text-[#D4A95A] mb-2">{t.common.phone}</div>
                                <a href={`tel:${BUSINESS.phoneRaw}`} className="font-serif text-xl text-[#3D3D24] hover:text-[#D4A95A]">
                                    {BUSINESS.phone}
                                </a>
                            </div>
                        </div>

                        <div data-testid="contact-info-instagram" className="flex items-start gap-5 pb-8 border-b border-[#3D3D24]/15">
                            <div className="w-12 h-12 bg-[#3D3D24] text-[#D4A95A] flex items-center justify-center shrink-0">
                                <Instagram size={18} />
                            </div>
                            <div>
                                <div className="font-label text-[10px] text-[#D4A95A] mb-2">{t.common.instagram}</div>
                                <div className="space-y-1">
                                    <a href={BUSINESS.instagram1} target="_blank" rel="noopener noreferrer" className="block text-[#3D3D24] hover:text-[#D4A95A]">{BUSINESS.instagram1Handle}</a>
                                    <a href={BUSINESS.instagram2} target="_blank" rel="noopener noreferrer" className="block text-[#3D3D24] hover:text-[#D4A95A]">{BUSINESS.instagram2Handle}</a>
                                </div>
                            </div>
                        </div>

                        <div data-testid="contact-info-tiktok" className="flex items-start gap-5 pb-8 border-b border-[#3D3D24]/15">
                            <div className="w-12 h-12 bg-[#3D3D24] text-[#D4A95A] flex items-center justify-center shrink-0">
                                <TikTokIcon size={18} />
                            </div>
                            <div>
                                <div className="font-label text-[10px] text-[#D4A95A] mb-2">TikTok</div>
                                <div className="space-y-1">
                                    <a href={BUSINESS.tiktok} target="_blank" rel="noopener noreferrer" className="block text-[#3D3D24] hover:text-[#D4A95A]">{BUSINESS.tiktokHandle}</a>
                                    <a href={BUSINESS.tiktokPersonal} target="_blank" rel="noopener noreferrer" className="block text-[#3D3D24] hover:text-[#D4A95A]">{BUSINESS.tiktokPersonalHandle}</a>
                                </div>
                            </div>
                        </div>

                        <div data-testid="contact-info-hours" className="flex items-start gap-5">
                            <div className="w-12 h-12 bg-[#3D3D24] text-[#D4A95A] flex items-center justify-center shrink-0">
                                <Clock size={18} />
                            </div>
                            <div className="flex-1">
                                <div className="font-label text-[10px] text-[#D4A95A] mb-3">{t.common.hours}</div>
                                <ul className="space-y-1.5 text-sm">
                                    {BUSINESS.hours.map((h) => (
                                        <li key={h.day} className="flex justify-between gap-6 max-w-[260px]">
                                            <span className="font-label text-[#3D3D24]">{h.day}</span>
                                            <span className="text-[#3D3D24]/80">{h.time}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Form */}
                    <div>
                        <div className="font-label text-[11px] text-[#D4A95A] mb-4">{c.formTitle}</div>
                        <h2 className="font-serif text-4xl md:text-5xl text-[#3D3D24] leading-[1.05] mb-8">
                            {t.common.sendMessage}
                        </h2>
                        <form
                            data-testid="contact-form"
                            onSubmit={onSubmit}
                            className="space-y-5 bg-[#F5F2E4] p-8 md:p-10"
                        >
                            {status.success && (
                                <div data-testid="contact-success" className="bg-[#3D3D24] text-[#FFFFF3] p-4 flex items-center gap-3 text-sm">
                                    <Check size={18} className="text-[#D4A95A]" />
                                    {t.common.thankYou}
                                </div>
                            )}
                            {status.error && (
                                <div data-testid="contact-error" className="bg-red-50 border border-red-200 text-red-700 p-4 text-sm">
                                    {status.error}
                                </div>
                            )}
                            <div className="grid sm:grid-cols-2 gap-4">
                                <Field testid="contact-name" label="Name" name="name" value={form.name} onChange={onChange} required />
                                <Field testid="contact-email" label="Email" type="email" name="email" value={form.email} onChange={onChange} required />
                            </div>
                            <Field testid="contact-phone" label="Phone" name="phone" value={form.phone} onChange={onChange} />
                            <div>
                                <label className="font-label text-[10px] text-[#3D3D24]/70 mb-2 block">
                                    Message<span className="text-[#D4A95A] ml-1">*</span>
                                </label>
                                <textarea
                                    data-testid="contact-message"
                                    name="message"
                                    value={form.message}
                                    onChange={onChange}
                                    required
                                    rows={5}
                                    className="w-full bg-[#FFFFF3] border border-[#3D3D24]/20 px-4 py-3 text-[#3D3D24] focus:border-[#3D3D24] outline-none resize-none"
                                />
                            </div>
                            <button
                                data-testid="contact-submit"
                                type="submit"
                                disabled={status.submitting}
                                className="w-full bg-[#3D3D24] text-[#FFFFF3] py-4 font-label text-xs hover:bg-[#D4A95A] hover:text-[#3D3D24] transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
                            >
                                {status.submitting ? <><Loader2 size={14} className="animate-spin" /> {t.common.submitting}</> : t.common.sendMessage}
                            </button>
                        </form>
                    </div>
                </div>

                {/* Map */}
                <div className="max-w-[1400px] mx-auto px-6 mt-20">
                    <div data-testid="contact-map" className="border border-[#3D3D24]/15 aspect-[16/7] overflow-hidden">
                        <iframe
                            title="Beauty Ink LV Location"
                            width="100%"
                            height="100%"
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            src={`https://www.google.com/maps?q=${encodeURIComponent(BUSINESS.address)}&output=embed`}
                        />
                    </div>
                </div>

                {/* Studio Location Gallery */}
                <div className="max-w-[1400px] mx-auto px-6 mt-20">
                    <div className="mb-8">
                        <div className="font-label text-[11px] text-[#D4A95A] mb-4">{lang === "es" ? "Nuestro Espacio" : "Our Space"}</div>
                        <h2 className="font-serif text-3xl md:text-4xl text-[#3D3D24] leading-[1.05]">
                            {lang === "es" ? "El Estudio" : "The Studio"}
                        </h2>
                    </div>
                    <div data-testid="contact-studio-gallery" className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                        {[
                            { src: IMG.bizLocation1, alt: "Beauty Ink LV studio entrance C-109 Las Vegas" },
                            { src: IMG.bizLocation2, alt: "Beauty Ink LV building exterior palm trees Las Vegas" },
                            { src: IMG.bizLocation4, alt: "Beauty Ink LV studio door sign by appointment only" },
                            { src: IMG.bizLocation3, alt: "Beauty Ink LV window cacti decor" },
                            { src: IMG.bizLocation5, alt: "Beauty Ink LV interior lounge area" },
                            { src: IMG.bizLocation6, alt: "Beauty Ink LV studio interior lamps" },
                            { src: IMG.bizLocation7, alt: "Beauty Ink LV waiting area lamp and mirror" },
                            { src: IMG.bizLocation9, alt: "Beauty Ink LV lounge area" },
                        ].map((item, i) => (
                            <div key={i} className="overflow-hidden aspect-square bg-[#F5F2E4]">
                                <img src={item.src} alt={item.alt} className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" />
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </>
    );
};

const Field = ({ testid, label, name, value, onChange, type = "text", required }) => (
    <div>
        <label className="font-label text-[10px] text-[#3D3D24]/70 mb-2 block">
            {label}{required && <span className="text-[#D4A95A] ml-1">*</span>}
        </label>
        <input
            data-testid={testid}
            type={type}
            name={name}
            value={value}
            onChange={onChange}
            required={required}
            className="w-full bg-[#FFFFF3] border border-[#3D3D24]/20 px-4 py-3 text-[#3D3D24] focus:border-[#3D3D24] outline-none"
        />
    </div>
);

export default Contact;
